import React, { useState, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Popup from '@atlaskit/popup'
import Avatar from '@atlaskit/avatar'
import Textfield from '@atlaskit/textfield'
import Tooltip from '@atlaskit/tooltip'
import IssuesIcon from '@atlaskit/icon/glyph/issues'
import BookIcon from '@atlaskit/icon/glyph/book'
import SettingsIcon from '@atlaskit/icon/glyph/settings'
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle'
import ChevronLeftIcon from '@atlaskit/icon/glyph/chevron-left'
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right'
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down'
import SearchIcon from '@atlaskit/icon/glyph/search'
import EditorDoneIcon from '@atlaskit/icon/glyph/editor/done'
import { SupportModal } from './SupportModal'

/**
 * GLOBAL SIDEBAR COMPONENT
 *
 * Main navigation sidebar for the Test Management application.
 * Collapse button is positioned in the top-left corner (Jira-style).
 *
 * Features:
 * - Collapse/expand button in top-left
 * - Rich project card selector with avatar, name, key and searchable popup picker
 * - Three menu items: Test Management, Library, Settings
 * - Tooltip hints when collapsed
 */
function GlobalSidebar({
  isCollapsed,
  onToggleCollapse,
  selectedProject,
  projects,
  onProjectChange
}) {
  const navigate = useNavigate()
  const location = useLocation()

  // State for project selector popup visibility
  const [isProjectPopupOpen, setIsProjectPopupOpen] = useState(false)

  // State for search/filter text in the project popup
  const [projectSearchQuery, setProjectSearchQuery] = useState('')

  // Ref for autofocusing the search textfield when popup opens
  const searchInputRef = useRef(null)

  // State for the Support modal (docs links + contact email) visibility
  const [isSupportOpen, setIsSupportOpen] = useState(false)

  // Determine which menu item is active based on current route
  const isTestManagementActive = location.pathname.startsWith('/releases') || location.pathname === '/'
  const isLibraryActive = location.pathname.startsWith('/library')
  const isSettingsActive = location.pathname.startsWith('/settings')

  /**
   * Get the display name for a project.
   * Prefers the dedicated 'name' field, falls back to extracting from 'label'.
   */
  const getProjectDisplayName = (project) => {
    if (project.name) return project.name
    // Fallback: strip "(KEY)" suffix from label if present
    if (project.label && project.key) {
      return project.label.replace(` (${project.key})`, '')
    }
    return project.label || 'Unknown'
  }

  /**
   * Filter projects based on search query.
   * Matches against both project name and key (case-insensitive).
   */
  const filteredProjects = projects.filter(project => {
    if (!projectSearchQuery.trim()) return true
    const query = projectSearchQuery.toLowerCase()
    const nameMatch = (project.name || project.label || '').toLowerCase().includes(query)
    const keyMatch = (project.key || '').toLowerCase().includes(query)
    return nameMatch || keyMatch
  })

  /**
   * Handle opening the project selector popup.
   * Resets search query on each open for fresh filtering.
   */
  const handleOpenProjectPopup = useCallback(() => {
    setProjectSearchQuery('')
    setIsProjectPopupOpen(true)
    // Focus search input after popup renders (slight delay for DOM mount)
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 50)
  }, [])

  /**
   * Handle selecting a project from the popup list.
   * Calls the parent's onProjectChange and closes the popup.
   */
  const handleSelectProject = useCallback((project) => {
    onProjectChange(project)
    setIsProjectPopupOpen(false)
    setProjectSearchQuery('')
  }, [onProjectChange])

  // Menu items configuration
  const menuItems = [
    {
      icon: <IssuesIcon label="Test Management" size="medium" />,
      label: 'Test Management',
      isActive: isTestManagementActive,
      onClick: () => navigate('/releases')
    },
    {
      icon: <BookIcon label="Library" size="medium" />,
      label: 'Library',
      isActive: isLibraryActive,
      onClick: () => navigate('/library')
    },
    {
      icon: <SettingsIcon label="Settings" size="medium" />,
      label: 'Settings',
      isActive: isSettingsActive,
      onClick: () => navigate('/settings')
    },
    {
      // Support opens a modal (docs + contact email) instead of navigating to a route
      icon: <QuestionCircleIcon label="Support" size="medium" />,
      label: 'Support',
      isActive: false,
      onClick: () => setIsSupportOpen(true)
    }
  ]

  // Render a single menu item
  const renderMenuItem = (item, index) => {
    const menuButton = (
      <button
        key={index}
        onClick={item.onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          gap: '12px',
          width: '100%',
          padding: isCollapsed ? '10px' : '10px 12px',
          border: 'none',
          borderRadius: '4px',
          backgroundColor: item.isActive
            ? 'var(--ds-background-selected, #DEEBFF)'
            : 'transparent',
          color: item.isActive
            ? 'var(--ds-text-selected, #0052CC)'
            : 'var(--ds-text, #172B4D)',
          fontSize: '14px',
          fontWeight: item.isActive ? 600 : 400,
          cursor: 'pointer',
          transition: 'background-color 0.15s ease',
          textAlign: 'left'
        }}
        onMouseEnter={(e) => {
          if (!item.isActive) {
            e.currentTarget.style.backgroundColor = 'var(--ds-background-neutral-hovered, #EBECF0)'
          }
        }}
        onMouseLeave={(e) => {
          if (!item.isActive) {
            e.currentTarget.style.backgroundColor = 'transparent'
          }
        }}
      >
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {item.icon}
        </span>
        {!isCollapsed && <span>{item.label}</span>}
      </button>
    )

    // Wrap in tooltip when collapsed
    if (isCollapsed) {
      return (
        <Tooltip key={index} content={item.label} position="right">
          {menuButton}
        </Tooltip>
      )
    }

    return menuButton
  }

  /**
   * Renders the popup content with search field and scrollable project list.
   * Each project option shows avatar, name, key, and checkmark if selected.
   */
  const renderProjectPopupContent = () => (
    <div
      className="project-selector-popup"
      style={{
        width: '240px',
        maxHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
        borderRadius: '4px',
        overflow: 'hidden'
      }}
    >
      {/* Search field at top of popup for filtering projects */}
      <div style={{
        padding: '8px',
        borderBottom: '1px solid var(--ds-border, #DFE1E6)'
      }}>
        <Textfield
          ref={searchInputRef}
          value={projectSearchQuery}
          onChange={(e) => setProjectSearchQuery(e.target.value)}
          placeholder="Search projects..."
          elemBeforeInput={
            <div style={{
              paddingLeft: '8px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <SearchIcon
                label="Search"
                size="small"
                primaryColor="var(--ds-text-subtlest, #6B778C)"
              />
            </div>
          }
          isCompact
          autoFocus
        />
      </div>

      {/* Scrollable project options list */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '4px 0'
      }}>
        {filteredProjects.length === 0 ? (
          /* Empty state when no projects match the search query */
          <div style={{
            padding: '16px',
            textAlign: 'center',
            color: 'var(--ds-text-subtlest, #6B778C)',
            fontSize: '13px'
          }}>
            No projects found
          </div>
        ) : (
          filteredProjects.map((project) => {
            /* Check if this project is currently selected (defensive ID comparison) */
            const isSelected = selectedProject &&
              (project.value === selectedProject.value ||
               String(project.value) === String(selectedProject.value))

            return (
              <button
                key={project.value}
                onClick={() => handleSelectProject(project)}
                className="project-option-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  backgroundColor: isSelected
                    ? 'var(--ds-background-selected, #DEEBFF)'
                    : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.1s ease',
                  fontFamily: 'inherit'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor =
                      'var(--ds-background-neutral-subtle-hovered, #F4F5F7)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  } else {
                    e.currentTarget.style.backgroundColor =
                      'var(--ds-background-selected, #DEEBFF)'
                  }
                }}
              >
                {/* Project avatar in the option row */}
                <Avatar
                  size="xsmall"
                  src={project.avatarUrl || undefined}
                  name={getProjectDisplayName(project)}
                  appearance="square"
                />

                {/* Project name and key stacked vertically */}
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--ds-text, #172B4D)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: '18px'
                  }}>
                    {getProjectDisplayName(project)}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--ds-text-subtlest, #6B778C)',
                    lineHeight: '14px'
                  }}>
                    {project.key}
                  </div>
                </div>

                {/* Checkmark icon for the currently selected project */}
                {isSelected && (
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    color: 'var(--ds-icon-selected, #0052CC)'
                  }}>
                    <EditorDoneIcon label="Selected" size="small" />
                  </span>
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )

  return (
    <div
      className="global-sidebar"
      style={{
        width: isCollapsed ? '64px' : '240px',
        minWidth: isCollapsed ? '64px' : '240px',
        height: '100vh',
        borderRight: '1px solid var(--ds-border, #DFE1E6)',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden'
      }}
    >
      {/* TOP - Collapse Button */}
      <div
        style={{
          padding: isCollapsed ? '12px 8px' : '12px 16px',
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-start'
        }}
      >
        <Tooltip content={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} position="right">
          <button
            onClick={onToggleCollapse}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: 'var(--ds-text-subtle, #5E6C84)',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--ds-background-neutral-hovered, #EBECF0)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            {isCollapsed ? (
              <ChevronRightIcon label="Expand" size="medium" />
            ) : (
              <ChevronLeftIcon label="Collapse" size="medium" />
            )}
          </button>
        </Tooltip>
      </div>

      {/* HEADER - Project Selector */}
      <div
        style={{
          padding: isCollapsed ? '8px' : '8px 16px 16px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)'
        }}
      >
        {/* Project Selector - Rich card trigger with popup when expanded */}
        {!isCollapsed && (
          <Popup
            isOpen={isProjectPopupOpen}
            onClose={() => {
              setIsProjectPopupOpen(false)
              setProjectSearchQuery('')
            }}
            placement="bottom-start"
            shouldRenderToParent
            content={renderProjectPopupContent}
            trigger={(triggerProps) => (
              <div>
                {/* "PROJECT" label above the card trigger */}
                <label style={{
                  fontSize: '11px',
                  color: 'var(--ds-text-subtlest, #6B778C)',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginBottom: '6px',
                  display: 'block'
                }}>
                  Project
                </label>

                {/* Clickable project card trigger - shows avatar, name, key, and chevron */}
                <button
                  {...triggerProps}
                  onClick={handleOpenProjectPopup}
                  className="project-card-trigger"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid var(--ds-border, #DFE1E6)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--ds-surface, #FFFFFF)',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    textAlign: 'left',
                    fontFamily: 'inherit'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--ds-background-neutral-subtle-hovered, #F4F5F7)'
                    e.currentTarget.style.borderColor = 'var(--ds-border-bold, #8993A4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--ds-surface, #FFFFFF)'
                    e.currentTarget.style.borderColor = 'var(--ds-border, #DFE1E6)'
                  }}
                >
                  {/* Project avatar - uses Jira avatar URL or falls back to initials */}
                  {selectedProject ? (
                    <Avatar
                      size="small"
                      src={selectedProject.avatarUrl || undefined}
                      name={getProjectDisplayName(selectedProject)}
                      appearance="square"
                    />
                  ) : (
                    <Avatar
                      size="small"
                      name="?"
                      appearance="square"
                    />
                  )}

                  {/* Project name and key stacked vertically */}
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    {selectedProject ? (
                      <>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--ds-text, #172B4D)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: '18px'
                        }}>
                          {getProjectDisplayName(selectedProject)}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: 'var(--ds-text-subtlest, #6B778C)',
                          lineHeight: '16px'
                        }}>
                          {selectedProject.key}
                        </div>
                      </>
                    ) : (
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--ds-text-subtlest, #6B778C)'
                      }}>
                        Select project...
                      </div>
                    )}
                  </div>

                  {/* Chevron-down icon indicating the card is clickable */}
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    color: 'var(--ds-text-subtle, #5E6C84)'
                  }}>
                    <ChevronDownIcon label="" size="medium" />
                  </span>
                </button>
              </div>
            )}
          />
        )}

        {/* Collapsed state - Project avatar badge or initials */}
        {isCollapsed && selectedProject && (
          <Tooltip content={selectedProject.label} position="right">
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onClick={onToggleCollapse}
            >
              {selectedProject.avatarUrl ? (
                /* Use Atlaskit Avatar with real project avatar from Jira */
                <Avatar
                  size="large"
                  src={selectedProject.avatarUrl}
                  name={getProjectDisplayName(selectedProject)}
                  appearance="square"
                />
              ) : (
                /* Fallback to initials badge when no avatar URL (dev mode) */
                <span style={{
                  fontSize: '14px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: 'var(--ds-background-brand-bold, #0052CC)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600
                }}>
                  {selectedProject.key?.substring(0, 2) || selectedProject.label?.charAt(0) || 'P'}
                </span>
              )}
            </div>
          </Tooltip>
        )}

        {/* Collapsed state - No project selected */}
        {isCollapsed && !selectedProject && (
          <Tooltip content="Select a project" position="right">
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onClick={onToggleCollapse}
            >
              <span style={{
                fontSize: '14px',
                width: '40px',
                height: '40px',
                borderRadius: '6px',
                backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                color: 'var(--ds-text-subtlest, #6B778C)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600
              }}>
                ?
              </span>
            </div>
          </Tooltip>
        )}
      </div>

      {/* MENU ITEMS */}
      <div
        style={{
          flex: 1,
          padding: isCollapsed ? '12px 8px' : '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}
      >
        {menuItems.map((item, index) => renderMenuItem(item, index))}
      </div>

      {/* BOTTOM - Zen Logo */}
      <div
        style={{
          padding: isCollapsed ? '12px 8px' : '12px 16px',
          borderTop: '1px solid var(--ds-border, #DFE1E6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {isCollapsed ? (
          <img
            src="/zen_icon.png"
            alt="Zen"
            style={{ width: '32px', height: '32px', objectFit: 'contain', mixBlendMode: 'multiply' }}
          />
        ) : (
          <img
            src="/Zen_text_logo.png"
            alt="Zen"
            style={{ width: '120px', objectFit: 'contain', mixBlendMode: 'multiply' }}
          />
        )}
      </div>

      {/* Support modal — rendered via Headless UI portal, toggled by the Support menu item */}
      <SupportModal
        isOpen={isSupportOpen}
        onClose={() => setIsSupportOpen(false)}
      />
    </div>
  )
}

export { GlobalSidebar }
